"use client";
import {
  Calendar,
  ChevronRight,
  Heart,
  Linkedin,
  MessageSquare,
  Repeat,
  Users,
  CircleDot,
} from "lucide-react";
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from "@/shadcnComponents/ui/avatar";
import { Badge } from "@/shadcnComponents/ui/badge";
import { Button } from "@/shadcnComponents/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  // CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/shadcnComponents/ui/card";
import { Progress } from "@/shadcnComponents/ui/progress";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/shadcnComponents/ui/tooltip";

import { Link, useNavigate } from "react-router-dom";
import { useCallback, useEffect, useState } from "react";
import { useBrand } from "@/Providers/Brand";
import { Skeleton } from "@/shadcnComponents/ui/skeleton";
import { OpenInNewWindow } from "@/Helpers/helper";

type InfluencerData = Array<{
  id: string;
  name: string;
  // handle: string;
  avatar?: string;
  followers: number;
  liveDate: string;
  avgLikes: number;
  avgComments: number;
  avgEngagement: number;
  linkedinProfile: string;
  submittedDate: string;
  draftPreview: string;
}>;

interface CampaignData {
  id: string;
  name: string;
  startDate: string;
  endDate: string;
  status: string;
  budget: {
    total: number;
    utilized: number;
  };
  metrics: {
    totalReach: number;
    totalLikes: number;
    totalComments: number;
    totalReposts: number;
    avgEngagement: number;
  };
  influencers: {
    total: InfluencerData;
    live: InfluencerData;
    upcoming: InfluencerData;
    draftsSubmitted: InfluencerData;
    influencersWithoutDate: Array<{
      id: string;
      name: string;
      handle: string;
      avatar?: string;
      followers: number;
    }>;
    totalSuggestions: number;
    approved: InfluencerData;
  };
  isLive: boolean;
  isCompleted: boolean;
  isReview: boolean;
}

// Group upcoming influencers by date
const groupInfluencersByDate = (influencers: InfluencerData) => {
  return influencers.reduce(
    (groups: Record<string, InfluencerData>, influencer) => {
      const date = influencer.liveDate;
      if (!groups[date]) {
        groups[date] = [];
      }
      groups[date].push(influencer);
      return groups;
    },
    {}
  );
};

export function LivePerformanceBoard() {
  const [campaignData, setCampaignData] = useState<CampaignData[]>([]);
  const [loading, setLoading] = useState(true);

  const brandState = useBrand();

  const getLivedDashboardData = useCallback(async () => {
    setLoading(true);
    const response = await brandState.getLivedDashboardData();
    if (response?.success) {
      setCampaignData(response?.data);
      setLoading(false);
    } else {
      // Handle error
      console.error("Failed to fetch data");
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    getLivedDashboardData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="flex flex-col">
      <div className="flex-1">
        {loading ? (
          <CampaignCardSkeleton />
        ) : (
          <div className="flex flex-col gap-4">
            {campaignData?.map((campaign) => (
              <LiveCampaignCard key={campaign?.id} campaignData={campaign} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

const LiveCampaignCard = ({ campaignData }: { campaignData: CampaignData }) => {
  const navigate = useNavigate();

  const upcomingInfluencersByDate = groupInfluencersByDate(
    campaignData.influencers.upcoming
  );

  const formatNumber = (num: number) => {
    return num ? num?.toLocaleString("en-IN") : "-";
  };

  // Format followers in K or M format
  const formatFollowers = (num: number) => {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + "M";
    } else if (num >= 1000) {
      return (num / 1000).toFixed(1) + "K";
    }
    return num.toString();
  };

  // Format date to readable format
  const formatDate = (dateString: string) => {
    const options: Intl.DateTimeFormatOptions = {
      year: "numeric",
      month: "short",
      day: "numeric",
    };
    return new Date(dateString).toLocaleDateString("en-US", options);
  };
  return (
    <>
      {(campaignData?.isLive || campaignData?.isCompleted) && (
        <Card className="mb-0">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-2xl">{campaignData?.name}</CardTitle>
                {/* <CardDescription>
                  {formatDate(campaignData?.startDate)} -{" "}
                  {formatDate(campaignData?.endDate)}
                </CardDescription> */}
              </div>
              <Badge
                className={`text-sm px-3 py-1 ${
                  campaignData?.isCompleted
                    ? "bg-blue-500 hover:bg-blue-600"
                    : "bg-green-400 hover:bg-green-500"
                }  text-white border-white-700`}
              >
                {campaignData?.isLive && "Live"}
                {campaignData?.isCompleted && "Completed"}
                {campaignData?.isReview && "In Review"}
              </Badge>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {/* Metrics Card */}
              {campaignData?.metrics && (
                <Card className="overflow-hidden  shadow-sm border-muted">
                  <CardHeader className="p-4">
                    <CardTitle className="text-lg">Campaign Metrics</CardTitle>
                  </CardHeader>
                  <CardContent className="p-4 pt-0">
                    <div className="grid grid-cols-2 gap-3">
                      <div className="bg-muted rounded-lg p-3 text-center">
                        <Users className="h-4 w-4 mx-auto mb-1 text-blue-500" />
                        <p className="text-xs text-muted-foreground">Reach</p>
                        <p className="text-sm font-semibold">
                          {formatNumber(campaignData?.metrics?.totalReach)}
                        </p>
                      </div>
                      <div className="bg-muted rounded-lg p-3 text-center">
                        <Heart className="h-4 w-4 mx-auto mb-1 text-red-500" />
                        <p className="text-xs text-muted-foreground">Likes</p>
                        <p className="text-sm font-semibold">
                          {formatNumber(campaignData?.metrics?.totalLikes)}
                        </p>
                      </div>
                      <div className="bg-muted rounded-lg p-3 text-center">
                        <MessageSquare className="h-4 w-4 mx-auto mb-1 text-green-500" />
                        <p className="text-xs text-muted-foreground">
                          Comments
                        </p>
                        <p className="text-sm font-semibold">
                          {formatNumber(campaignData?.metrics?.totalComments)}
                        </p>
                      </div>
                      <div className="bg-muted rounded-lg p-3 text-center">
                        <Repeat className="h-4 w-4 mx-auto mb-1 text-purple-500" />
                        <p className="text-xs text-muted-foreground">Reposts</p>
                        <p className="text-sm font-semibold">
                          {formatNumber(campaignData?.metrics?.totalReposts)}
                        </p>
                      </div>
                    </div>
                    <div className="mt-3 bg-muted rounded-lg p-3 text-center">
                      <p className="text-xs text-muted-foreground">
                        Engagement Rate
                      </p>
                      <p className="text-lg font-semibold">
                        {campaignData?.metrics?.avgEngagement}%
                      </p>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Live Influencers Card */}
              {campaignData?.influencers?.live?.length > 0 && (
                <Card className="overflow-hidden  shadow-sm border-muted">
                  <CardHeader className="p-4">
                    <CardTitle className="text-lg">Live Influencers</CardTitle>
                  </CardHeader>
                  <CardContent className="p-4 pt-0">
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-muted-foreground">
                          {campaignData?.influencers?.live?.length}/
                          {campaignData?.influencers?.total?.length} Live
                        </span>
                        <span className="text-sm font-medium">
                          {Math.round(
                            (campaignData?.influencers?.live?.length /
                              campaignData?.influencers?.total?.length) *
                              100
                          )}
                          %
                        </span>
                      </div>
                      <Progress
                        value={
                          (campaignData?.influencers?.live?.length /
                            campaignData?.influencers?.total?.length) *
                          100
                        }
                        className="h-2 bg-green-100 [&>div]:bg-green-500"
                      />
                      <div className="flex flex-wrap gap-2 mt-4">
                        <TooltipProvider>
                          {campaignData?.influencers?.live?.map(
                            (influencer) => (
                              <Tooltip key={influencer?.id}>
                                <TooltipTrigger asChild>
                                  <Avatar className="h-10 w-10 border-2 border-white hover:ring-2 hover:ring-primary transition-all">
                                    <AvatarImage
                                      src={
                                        influencer?.avatar || "/placeholder.svg"
                                      }
                                      alt={influencer?.name}
                                    />
                                    <AvatarFallback>
                                      {influencer?.name?.substring(0, 2)}
                                    </AvatarFallback>
                                  </Avatar>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <div className="space-y-1">
                                    <p className="font-medium">
                                      {influencer?.name}
                                    </p>
                                    {/* <p className="text-xs text-muted-foreground">
                                      {influencer?.handle}
                                    </p> */}
                                    <p className="text-xs">
                                      Live on:{" "}
                                      {formatDate(influencer?.liveDate)}
                                    </p>
                                  </div>
                                </TooltipContent>
                              </Tooltip>
                            )
                          )}
                        </TooltipProvider>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Budget Utilization Card */}
              {campaignData?.budget && (
                <Card className="overflow-hidden  shadow-sm border-muted">
                  <CardHeader className="p-4">
                    <CardTitle className="text-lg">
                      Budget Utilization
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-4 pt-0">
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-muted-foreground">
                          Utilized Amount
                        </span>
                        <span className="text-lg font-semibold">
                          ₹{formatNumber(campaignData?.budget?.utilized)}
                        </span>
                      </div>
                      <Progress
                        value={
                          (campaignData?.budget?.utilized /
                            campaignData?.budget?.total) *
                          100
                        }
                        className="h-2 bg-blue-100 [&>div]:bg-blue-500"
                      />
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground">
                          {Math.round(
                            (campaignData?.budget?.utilized /
                              campaignData?.budget?.total) *
                              100
                          )}
                          % utilized
                        </span>
                      </div>
                      <div className="grid grid-cols-2 gap-4 mt-2">
                        <div>
                          <p className="text-sm text-muted-foreground">
                            Total Budget
                          </p>
                          <p className="font-medium">
                            ₹{formatNumber(campaignData?.budget?.total)}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm text-muted-foreground">
                            Remaining
                          </p>
                          <p className="font-medium">
                            ₹
                            {formatNumber(
                              campaignData?.budget?.total -
                                campaignData?.budget?.utilized
                            )}
                          </p>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Upcoming Influencers Card */}
              {campaignData?.influencers?.upcoming?.length > 0 && (
                <Card className="overflow-hidden  md:col-span-2 lg:col-span-2 shadow-sm border-muted">
                  <CardHeader className="p-4">
                    <CardTitle className="text-lg">
                      Upcoming Live Influencers
                    </CardTitle>
                  </CardHeader>
                  <CardContent
                    className="p-4 pt-0 overflow-auto"
                    style={{ maxHeight: "165px" }}
                  >
                    <div className="space-y-3">
                      {Object.entries(upcomingInfluencersByDate)?.map(
                        ([date, influencers]) => (
                          <div key={date}>
                            <h4 className="text-sm font-medium flex items-center gap-1 mb-2">
                              <Calendar className="h-3 w-3" />
                              {formatDate(date)}
                            </h4>
                            <div className="grid gap-2">
                              {influencers?.map((influencer) => (
                                <div
                                  key={influencer.id}
                                  className="flex items-center justify-between p-2 rounded-md border bg-card"
                                >
                                  <div className="flex items-center gap-2">
                                    <Avatar className="h-8 w-8">
                                      <AvatarImage
                                        src={
                                          influencer?.avatar ||
                                          "/placeholder.svg"
                                        }
                                        alt={influencer?.name}
                                      />
                                      <AvatarFallback>
                                        {influencer?.name?.substring(0, 2)}
                                      </AvatarFallback>
                                    </Avatar>
                                    <div>
                                      <p className="text-sm font-medium">
                                        {influencer?.name}
                                      </p>
                                      <div className="flex items-center gap-1 text-xs text-muted-foreground">
                                        <Linkedin className="h-3 w-3" />
                                        <span>
                                          {formatFollowers(
                                            influencer?.followers
                                          )}
                                        </span>
                                      </div>
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <Badge
                                      variant="outline"
                                      className="bg-amber-50 text-amber-600 border-amber-200 flex items-center gap-1"
                                    >
                                      <CircleDot className="h-3 w-3 text-amber-500" />
                                      <span>Upcoming</span>
                                    </Badge>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )
                      )}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Influencers Without Live Date Card */}
              {campaignData?.influencers?.influencersWithoutDate?.length >
                0 && (
                <Card className="overflow-hidden  lg:col-span-1 shadow-sm border-muted">
                  <CardHeader className="p-4">
                    <CardTitle className="text-lg">Live Date Not Set</CardTitle>
                  </CardHeader>
                  <CardContent
                    className="p-4 pt-0 overflow-auto"
                    style={{ maxHeight: "165px" }}
                  >
                    <div className="space-y-3">
                      {campaignData?.influencers?.influencersWithoutDate?.map(
                        (influencer, index) => (
                          <div
                            key={influencer?.id}
                            className="flex items-center justify-between p-2 rounded-md border bg-card"
                          >
                            <div className="flex items-center gap-2">
                              <div className="flex-shrink-0 w-5 text-center text-xs text-muted-foreground">
                                {index + 1}.
                              </div>
                              <Avatar className="h-7 w-7">
                                <AvatarImage
                                  src={influencer?.avatar || "/placeholder.svg"}
                                  alt={influencer?.name}
                                />
                                <AvatarFallback>
                                  {influencer?.name?.substring(0, 2)}
                                </AvatarFallback>
                              </Avatar>
                              <div>
                                <p className="text-sm font-medium">
                                  {influencer?.name}
                                </p>
                                <div className="flex items-center gap-1 text-xs text-muted-foreground">
                                  <Linkedin className="h-3 w-3" />
                                  <span>
                                    {formatFollowers(influencer?.followers)}
                                  </span>
                                </div>
                              </div>
                            </div>
                            <div className="flex flex-col items-end gap-1">
                              <Button
                                onClick={() => {
                                  navigate(
                                    `/Brand/DashBoard/AllCampaign/Campaign/${campaignData.id}/?section=Review`
                                  );
                                }}
                                variant="outline"
                                size="sm"
                                className="h-6 text-xs"
                              >
                                Set Live Date
                              </Button>
                            </div>
                          </div>
                        )
                      )}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </CardContent>
          <CardFooter className="bg-muted/30 px-6 py-4">
            <Button
              asChild
              className="ml-auto bg-red-600 hover:bg-red-700 text-white"
            >
              <Link
                to={`/Brand/DashBoard/AllCampaign/Campaign/${
                  campaignData.id
                }/?section=${campaignData?.isReview ? "Review" : "Live"}`}
              >
                View Full Campaign Details{" "}
                <ChevronRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </CardFooter>
        </Card>
      )}

      {campaignData?.isReview && (
        <Card className="mb-6 shadow-md border-orange-100">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-2xl">{campaignData?.name}</CardTitle>
                <CardDescription>
                  Expected: {formatDate(campaignData?.startDate)} -{" "}
                  {formatDate(campaignData?.endDate)}
                </CardDescription>
              </div>
              <Badge className="text-sm px-3 py-1 bg-orange-500 hover:bg-orange-600 text-white border-orange-700">
                {campaignData?.isReview && "In Review"}
              </Badge>
            </div>

            {/* Review Status Summary */}
            <div className="grid grid-cols-4 gap-4 mt-4 pt-4 border-t">
              <div className="text-center">
                <span className="text-2xl font-bold text-blue-600">
                  {campaignData?.influencers?.draftsSubmitted?.length}
                </span>
                <p className="text-sm text-muted-foreground">
                  Drafts Submitted
                </p>
              </div>
              <div className="text-center">
                <span className="text-2xl font-bold text-purple-600">
                  {campaignData?.influencers?.totalSuggestions}
                </span>
                <p className="text-sm text-muted-foreground">
                  Suggestions Added
                </p>
              </div>
              <div className="text-center">
                <span className="text-2xl font-bold text-green-600">
                  {campaignData?.influencers?.approved?.length}
                </span>
                <p className="text-sm text-muted-foreground">Approved</p>
              </div>
              <div className="text-center">
                <span className="text-2xl font-bold text-orange-600">
                  {campaignData?.influencers.draftsSubmitted.length -
                    campaignData?.influencers?.approved?.length}
                </span>
                <p className="text-sm text-muted-foreground">Pending Review</p>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {/* Selected Influencers Card */}
              <Card className="overflow-hidden h-[300px] shadow-sm border-muted">
                <CardHeader className="p-4">
                  <CardTitle className="text-lg">
                    Selected Influencers
                  </CardTitle>
                  <CardDescription className="text-sm">
                    {campaignData?.influencers?.total?.length} total selected
                  </CardDescription>
                </CardHeader>
                <CardContent className="p-4 pt-0">
                  <div className="overflow-auto" style={{ maxHeight: "200px" }}>
                    <div className="grid grid-cols-4 gap-2">
                      <TooltipProvider>
                        {campaignData?.influencers?.total?.map((influencer) => (
                          <Tooltip key={influencer.id}>
                            <TooltipTrigger asChild>
                              <Avatar className="h-12 w-12 border-2 border-white hover:ring-2 hover:ring-primary transition-all cursor-pointer">
                                <AvatarImage
                                  src={influencer?.avatar || "/placeholder.svg"}
                                  alt={influencer?.name}
                                />
                                <AvatarFallback className="text-xs">
                                  {influencer?.name?.substring(0, 2)}
                                </AvatarFallback>
                              </Avatar>
                            </TooltipTrigger>
                            <TooltipContent className="w-64">
                              <div className="space-y-2">
                                <div className="flex items-center gap-2">
                                  <Avatar className="h-8 w-8">
                                    <AvatarImage
                                      src={
                                        influencer?.avatar || "/placeholder.svg"
                                      }
                                      alt={influencer?.name}
                                    />
                                    <AvatarFallback>
                                      {influencer?.name.substring(0, 2)}
                                    </AvatarFallback>
                                  </Avatar>
                                  <div>
                                    <p className="font-medium">
                                      {influencer?.name}
                                    </p>
                                    {/* <p className="text-xs text-muted-foreground">{influencer?.handle}</p> */}
                                  </div>
                                </div>
                                <div className="grid grid-cols-2 gap-2 text-xs">
                                  <div>
                                    <p className="text-muted-foreground">
                                      Followers
                                    </p>
                                    <p className="font-medium">
                                      {formatFollowers(influencer?.followers)}
                                    </p>
                                  </div>
                                  <div>
                                    <p className="text-muted-foreground">
                                      Avg Likes
                                    </p>
                                    <p className="font-medium">
                                      {formatNumber(influencer?.avgLikes)}
                                    </p>
                                  </div>
                                  <div>
                                    <p className="text-muted-foreground">
                                      Avg Comments
                                    </p>
                                    <p className="font-medium">
                                      {formatNumber(influencer?.avgComments)}
                                    </p>
                                  </div>
                                  <div>
                                    <p className="text-muted-foreground">
                                      Engagement
                                    </p>
                                    <p className="font-medium">
                                      {influencer?.avgEngagement}%
                                    </p>
                                  </div>
                                </div>
                                <Button
                                  onClick={() =>
                                    OpenInNewWindow({
                                      link: influencer?.linkedinProfile,
                                    })
                                  }
                                  size="sm"
                                  className="w-full"
                                >
                                  <Linkedin className="mr-1 h-3 w-3" />
                                  View LinkedIn
                                </Button>
                              </div>
                            </TooltipContent>
                          </Tooltip>
                        ))}
                      </TooltipProvider>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Draft Submitted Card */}
              <Card className="overflow-hidden h-[300px] shadow-sm border-muted">
                <CardHeader className="p-4">
                  <CardTitle className="text-lg">Drafts Submitted</CardTitle>
                  <CardDescription className="text-sm">
                    {campaignData?.influencers?.draftsSubmitted?.length} drafts
                    received
                  </CardDescription>
                </CardHeader>
                <CardContent className="p-4 pt-0">
                  <div className="overflow-auto" style={{ maxHeight: "200px" }}>
                    <div className="space-y-2">
                      <TooltipProvider>
                        {campaignData?.influencers?.draftsSubmitted?.map(
                          (influencer) => (
                            <Tooltip key={influencer?.id}>
                              <TooltipTrigger asChild>
                                <div className="flex items-center gap-2 p-2 rounded-md border bg-card hover:bg-muted/50 cursor-pointer transition-colors">
                                  <Avatar className="h-8 w-8">
                                    <AvatarImage
                                      src={
                                        influencer?.avatar || "/placeholder.svg"
                                      }
                                      alt={influencer?.name}
                                    />
                                    <AvatarFallback className="text-xs">
                                      {influencer?.name.substring(0, 2)}
                                    </AvatarFallback>
                                  </Avatar>
                                  <div className="flex-1 min-w-0">
                                    <p className="text-sm font-medium truncate">
                                      {influencer?.name}
                                    </p>
                                    <p className="text-xs text-muted-foreground">
                                      {formatDate(influencer?.submittedDate)}
                                    </p>
                                  </div>
                                  <Badge
                                    onClick={() =>
                                      navigate(
                                        `/Brand/DashBoard/AllCampaign/Campaign/${campaignData?.id}/?section=Review`
                                      )
                                    }
                                    variant="outline"
                                    className="text-xs"
                                  >
                                    Draft
                                  </Badge>
                                </div>
                              </TooltipTrigger>
                              <TooltipContent className="w-80">
                                <div className="space-y-3">
                                  <div className="flex items-center gap-2">
                                    <Avatar className="h-8 w-8">
                                      <AvatarImage
                                        src={
                                          influencer?.avatar ||
                                          "/placeholder.svg"
                                        }
                                        alt={influencer?.name}
                                      />
                                      <AvatarFallback>
                                        {influencer?.name.substring(0, 2)}
                                      </AvatarFallback>
                                    </Avatar>
                                    <div>
                                      <p className="font-medium">
                                        {influencer?.name}
                                      </p>
                                      {/* <p className="text-xs text-muted-foreground">{influencer?.handle}</p> */}
                                    </div>
                                  </div>
                                  <div className="grid grid-cols-2 gap-2 text-xs">
                                    <div>
                                      <p className="text-muted-foreground">
                                        Followers
                                      </p>
                                      <p className="font-medium">
                                        {formatFollowers(influencer?.followers)}
                                      </p>
                                    </div>
                                    <div>
                                      <p className="text-muted-foreground">
                                        Avg Likes
                                      </p>
                                      <p className="font-medium">
                                        {formatNumber(influencer?.avgLikes)}
                                      </p>
                                    </div>
                                    <div>
                                      <p className="text-muted-foreground">
                                        Avg Comments
                                      </p>
                                      <p className="font-medium">
                                        {formatNumber(influencer?.avgComments)}
                                      </p>
                                    </div>
                                    <div>
                                      <p className="text-muted-foreground">
                                        Engagement
                                      </p>
                                      <p className="font-medium">
                                        {influencer?.avgEngagement}%
                                      </p>
                                    </div>
                                  </div>
                                  <div className="border-t pt-2">
                                    <p className="text-xs text-muted-foreground mb-1">
                                      Draft Preview:
                                    </p>
                                    <p className="text-sm line-clamp-3">
                                      {influencer?.draftPreview}
                                    </p>
                                    <Button
                                      onClick={() =>
                                        navigate(
                                          `/Brand/DashBoard/AllCampaign/Campaign/${campaignData?.id}/?section=Review`
                                        )
                                      }
                                      variant="link"
                                      size="sm"
                                      className="p-0 h-auto text-xs mt-1"
                                    >
                                      View More
                                    </Button>
                                  </div>
                                </div>
                              </TooltipContent>
                            </Tooltip>
                          )
                        )}
                      </TooltipProvider>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Approved for Live Card */}
              <Card className="overflow-hidden h-[300px] shadow-sm border-muted">
                <CardHeader className="p-4">
                  <CardTitle className="text-lg">Approved for Live</CardTitle>
                  <CardDescription className="text-sm">
                    {campaignData?.influencers?.approved?.length} ready to go
                    live
                  </CardDescription>
                </CardHeader>
                <CardContent className="p-4 pt-0">
                  <div className="space-y-4">
                    <div
                      className="overflow-auto"
                      style={{ maxHeight: "160px" }}
                    >
                      <div className="space-y-2">
                        {campaignData?.influencers?.approved?.map(
                          (influencer) => (
                            <div
                              key={influencer.id}
                              className="flex items-center justify-between p-2 rounded-md border bg-card"
                            >
                              <div className="flex items-center gap-2">
                                <Avatar className="h-8 w-8">
                                  <AvatarImage
                                    src={
                                      influencer.avatar || "/placeholder.svg"
                                    }
                                    alt={influencer.name}
                                  />
                                  <AvatarFallback className="text-xs">
                                    {influencer.name.substring(0, 2)}
                                  </AvatarFallback>
                                </Avatar>
                                <div className="min-w-0 flex-1">
                                  <p className="text-sm font-medium truncate">
                                    {influencer.name}
                                  </p>
                                  <div className="flex items-center gap-1 text-xs text-muted-foreground">
                                    <Linkedin className="h-3 w-3" />
                                    <span>
                                      {formatFollowers(influencer.followers)}
                                    </span>
                                  </div>
                                </div>
                              </div>
                              <div className="flex flex-col items-end gap-1">
                                {influencer.liveDate ? (
                                  <div className="text-right">
                                    <Badge
                                      variant="outline"
                                      className="bg-green-50 text-green-600 border-green-200 text-xs"
                                    >
                                      {formatDate(influencer.liveDate)}
                                    </Badge>
                                  </div>
                                ) : (
                                  <Button
                                    onClick={() =>
                                      navigate(
                                        `/Brand/DashBoard/AllCampaign/Campaign/${campaignData?.id}/?section=Date`
                                      )
                                    }
                                    variant="outline"
                                    size="sm"
                                    className="h-6 text-xs px-2"
                                  >
                                    Set Live Date
                                  </Button>
                                )}
                              </div>
                            </div>
                          )
                        )}
                      </div>
                    </div>
                    <div className="pt-2 border-t">
                      <p className="text-xs text-muted-foreground text-center">
                        {
                          campaignData?.influencers?.approved?.filter(
                            (inf) => inf.liveDate
                          ).length
                        }{" "}
                        of {campaignData?.influencers?.approved?.length} have
                        live dates set
                      </p>
                      <Progress
                        value={
                          (campaignData?.influencers?.approved?.filter(
                            (inf) => inf.liveDate
                          ).length /
                            campaignData?.influencers?.approved?.length) *
                          100
                        }
                        className="h-2 mt-2 bg-green-100 [&>div]:bg-green-500"
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </CardContent>
          <CardFooter className="bg-muted/30 px-6 py-4">
            <Button
              asChild
              className="ml-auto bg-orange-600 hover:bg-orange-700 text-white"
            >
              <Link
                to={`/Brand/DashBoard/AllCampaign/Campaign/${campaignData?.id}/?section=Review`}
              >
                Go to Review Dashboard <ChevronRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </CardFooter>
        </Card>
      )}
    </>
  );
};

const CampaignCardSkeleton = () => {
  return (
    <Card className="mb-0">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="space-y-2">
            <Skeleton className="h-8 w-64" />
            <Skeleton className="h-4 w-32" />
          </div>
          <Skeleton className="h-6 w-24" />
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {/* Metrics Card Skeleton */}
          <Card className="overflow-hidden  shadow-sm border-muted">
            <CardHeader className="p-4">
              <Skeleton className="h-6 w-36" />
            </CardHeader>
            <CardContent className="p-4 pt-0">
              <div className="grid grid-cols-2 gap-3">
                {[1, 2, 3, 4].map((item) => (
                  <div
                    key={item}
                    className="bg-muted rounded-lg p-3 text-center"
                  >
                    <Skeleton className="h-4 w-4 mx-auto mb-1" />
                    <Skeleton className="h-3 w-16 mx-auto mb-1" />
                    <Skeleton className="h-4 w-12 mx-auto" />
                  </div>
                ))}
              </div>
              <div className="mt-3 bg-muted rounded-lg p-3 text-center">
                <Skeleton className="h-3 w-24 mx-auto mb-1" />
                <Skeleton className="h-6 w-12 mx-auto" />
              </div>
              <div className="mt-3">
                <Skeleton className="h-8 w-full" />
              </div>
            </CardContent>
          </Card>

          {/* Live Influencers Card Skeleton */}
          <Card className="overflow-hidden  shadow-sm border-muted">
            <CardHeader className="p-4">
              <Skeleton className="h-6 w-36" />
            </CardHeader>
            <CardContent className="p-4 pt-0">
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <Skeleton className="h-4 w-24" />
                  <Skeleton className="h-4 w-12" />
                </div>
                <Skeleton className="h-2 w-full" />
                <div className="flex flex-wrap gap-2 mt-4">
                  {[1, 2, 3, 4, 5, 6].map((item) => (
                    <Skeleton key={item} className="h-10 w-10 rounded-full" />
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Budget Utilization Card Skeleton */}
          <Card className="overflow-hidden  shadow-sm border-muted">
            <CardHeader className="p-4">
              <Skeleton className="h-6 w-36" />
            </CardHeader>
            <CardContent className="p-4 pt-0">
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <Skeleton className="h-4 w-28" />
                  <Skeleton className="h-6 w-20" />
                </div>
                <Skeleton className="h-2 w-full" />
                <div className="flex items-center justify-between text-sm">
                  <Skeleton className="h-4 w-20" />
                </div>
                <div className="grid grid-cols-2 gap-4 mt-2">
                  <div>
                    <Skeleton className="h-4 w-24 mb-2" />
                    <Skeleton className="h-5 w-16" />
                  </div>
                  <div>
                    <Skeleton className="h-4 w-20 mb-2" />
                    <Skeleton className="h-5 w-16" />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Upcoming Influencers Card Skeleton */}
          <Card className="overflow-hidden  md:col-span-2 lg:col-span-2 shadow-sm border-muted">
            <CardHeader className="p-4">
              <Skeleton className="h-6 w-48" />
            </CardHeader>
            <CardContent
              className="p-4 pt-0 overflow-auto"
              style={{ maxHeight: "165px" }}
            >
              <div className="space-y-3">
                {[1, 2].map((dateGroup) => (
                  <div key={dateGroup}>
                    <Skeleton className="h-4 w-32 mb-2" />
                    <div className="grid gap-2">
                      {[1, 2].map((item) => (
                        <div
                          key={item}
                          className="flex items-center justify-between p-2 rounded-md border bg-card"
                        >
                          <div className="flex items-center gap-2">
                            <Skeleton className="h-8 w-8 rounded-full" />
                            <div>
                              <Skeleton className="h-4 w-32 mb-1" />
                              <Skeleton className="h-3 w-20" />
                            </div>
                          </div>
                          <Skeleton className="h-6 w-24" />
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Influencers Without Live Date Skeleton */}
          <Card className="overflow-hidden  lg:col-span-1 shadow-sm border-muted">
            <CardHeader className="p-4">
              <Skeleton className="h-6 w-36" />
            </CardHeader>
            <CardContent
              className="p-4 pt-0 overflow-auto"
              style={{ maxHeight: "165px" }}
            >
              <div className="space-y-3">
                {[1, 2, 3].map((item) => (
                  <div
                    key={item}
                    className="flex items-center justify-between p-2 rounded-md border bg-card"
                  >
                    <div className="flex items-center gap-2">
                      <Skeleton className="h-4 w-4" />
                      <Skeleton className="h-7 w-7 rounded-full" />
                      <div>
                        <Skeleton className="h-4 w-24 mb-1" />
                        <Skeleton className="h-3 w-16" />
                      </div>
                    </div>
                    <Skeleton className="h-6 w-24" />
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </CardContent>
      <CardFooter className="bg-muted/30 px-6 py-4">
        <div className="ml-auto">
          <Skeleton className="h-10 w-48" />
        </div>
      </CardFooter>
    </Card>
  );
};
